import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { countries } from '../mock';
import { toast } from 'sonner';
import { submitLead, fetchGeoLocation } from '../services/api';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoLoading, setGeoLoading] = useState(true);

  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeoLocation = async () => {
      try {
        const geoData = await fetchGeoLocation();
        if (geoData?.data) {
          // Find matching country in our list
          const matchedCountry = countries.find(
            c => c.countryCode === geoData.data.countryCode
          );
          
          if (matchedCountry) {
            setFormData(prev => ({
              ...prev,
              countryCode: matchedCountry.code,
              country: matchedCountry.countryCode
            }));
          }
        }
      } catch (error) {
        console.error('Error loading geolocation:', error);
      } finally {
        setGeoLoading(false);
      }
    };

    loadGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (formData.phone.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const result = await submitLead(formData);

      if (result.success) {
        toast.success('Thank you for contacting us! We will get back to you soon.');
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          country: 'US',
          phone: '',
          message: ''
        });
      } else {
        toast.error(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Phone number validation: only allow digits
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, ''); // Remove non-digits
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleCountryChange = (value) => {
    const selectedCountry = countries.find(c => c.code === value);
    setFormData({
      ...formData,
      countryCode: value,
      country: selectedCountry?.countryCode || 'US'
    });
  };

  return (
    <div>
      <SEO 
        title="Contact IP Protection Center - Get Expert Trademark Help | Berkeley, CA"
        description="Contact IP Protection Center for professional trademark registration assistance. Located in Berkeley, CA. Call +1 (888) 449 0492 or email info@ipprotectioncenter.com. Expert support available."
        keywords="contact IP Protection Center, trademark help, trademark consultation, Berkeley CA, trademark support, IP attorney contact"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(177, 91, 55, 0.7)), url('/assets/img/inner-bg.jpg')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-white/90">
              Have questions? We're here to help. Reach out to our team for expert guidance on trademark and intellectual property services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto" data-aos="fade-up">
            {/* Contact Form */}
            <div data-aos="fade-right">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Hidden fields for IP info */}
                <input type="hidden" id="hiddenId" name="hiddenId" />
                <input type="hidden" className="IpInfo" name="ipInfo" />
                <input type="hidden" className="locerr" name="locerr" />
                
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Contact Number *</Label>
                  <div className="flex gap-2 mt-1" data-aos="fade-up">
                    <Select
                      value={formData.countryCode}
                      onValueChange={handleCountryChange}
                      disabled={geoLoading}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.countryCode} value={country.code}>
                            {country.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                      required
                      className="flex-1"
                    />
                  </div>
                  {geoLoading && (
                    <p className="text-xs text-gray-500 mt-1">Loading country...</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help you..."
                    rows={6}
                    required
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-brand-500 hover:bg-bblu7-0 text-white"
                  disabled={isSubmitting || geoLoading}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div data-aos="fade-left">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-lg text-gray-600 mb-8">
                Our team of trademark experts is ready to assist you with all your intellectual property needs. Contact us today to get started.
              </p>

              <div className="space-y-6">
                <div className="flex items-start" data-aos="zoom-in" data-aos="zoom-in" data-aos-delay="100">
                  <div className="bg-brand-100 rounded-full p-3 mr-4" data-aos="fade-up">
                    <Mail className="h-6 w-6 text-blue660" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">info@ipprotectioncenter.com</p>
                    <p className="text-gray-600">support@ipprotectioncenter.com</p>
                  </div>
                </div>

                <div className="flex items-start" data-aos="zoom-in" data-aos="zoom-in" data-aos-delay="200">
                  <div className="bg-brand-100 rounded-full p-3 mr-4" data-aos="fade-up">
                    <Phone className="h-6 w-6 text-blue660" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">+1 (888) 449 0492</p>
                    <p className="text-gray-600 text-sm">Monday - Friday, 9 AM - 6 PM EST</p>
                  </div>
                </div>

                <div className="flex items-start" data-aos="zoom-in" data-aos="zoom-in" data-aos-delay="200">
                  <div className="bg-brand-100 rounded-full p-3 mr-4" data-aos="fade-up">
                    <MapPin className="h-6 w-6 text-blue660" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-600">2247 Sixth St</p>
                    <p className="text-gray-600 text-sm">Berkeley, CA 94710, United States</p>
                  </div>
                </div>

                <div className="flex items-start" data-aos="zoom-in" data-aos="zoom-in" data-aos-delay="200">
                  <div className="bg-brand-100 rounded-full p-3 mr-4" data-aos="fade-up">
                    <Clock className="h-6 w-6 text-blue660" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                    <p className="text-gray-600">Saturday - Sunday: Closed</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-brand-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="font-semibold text-gray-900 mb-2">Quick Response</h3>
                <p className="text-gray-600">
                  We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;